<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ plano.id ? "Editar Plano" : "Novo Plano" }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm
      :submitText="plano.id ? 'Salvar' : 'Salvar'"
      :cancelText="'Cancelar'"
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarPlano"
      @cancel="voltar"
    >
      <!-- Dados do Tipo de Plano -->
      <FormSection title="Informações do Tipo de Plano">
        <div class="d-flex justify-content-between flex-wrap">
          <!-- Campo Nome -->
          <InputField
            id="nome"
            v-model="plano.nome"
            label="Nome <span class='required'>*</span>"
            :class="['me-3', { 'error-input': errors.nome }]"
            placeholder="Digite o nome do plano"
            required
            width="200px"
          />

          <!-- Campo Duração (pré-definida) -->
          <SelectField
            id="duracao"
            v-model="duracaoPredefinida"
            label="Duração <span class='required'>*</span>"
            :options="[
              { value: '', text: 'Selecione uma duração', disabled: true },
              ...opcoesDuracao.map(opcao => ({ value: String(opcao.value), text: opcao.text }))
            ]"
            :class="['me-3', { 'error-input': errors.duracao }]"
            :disabled="usarPersonalizada"
            required
          />
        <!-- Toggle + campos da duração personalizada -->
        <div class="mt-3">
          <div class="form-check form-switch mb-2">
            <input
              class="form-check-input"
              type="checkbox"
              id="checkPersonalizada"
              v-model="usarPersonalizada"
              @change="sincronizarDuracao"
            />
            <label class="form-check-label" for="checkPersonalizada">
              Usar duração personalizada
            </label>
          </div>

          <div v-if="usarPersonalizada" class="d-flex align-items-center flex-wrap" style="gap:10px;">
            <div class="form-group">
              <label for="qtd">Quantidade <span class="required">*</span></label>
              <input
                id="qtd"
                type="number"
                min="1"
                class="form-control"
                v-model.number="valorCustom"
                @input="sincronizarDuracao"
                placeholder="Digite a quantidade"
                style="max-width: 140px;"
              />
            </div>

            <div class="form-group">
              <label for="unid">Unidade <span class="required">*</span></label>
              <select
                id="unid"
                class="form-control"
                v-model="unidadeCustom"
                @change="sincronizarDuracao"
                style="max-width: 160px;"
              >
                <option v-for="u in unidades" :key="u.value" :value="u.value">{{ u.text }}</option>
              </select>
            </div>

            <small class="text-muted ms-2">
              Salvando como <strong>{{ duracaoEmDias || 0 }}</strong> dia(s)
            </small>
          </div>

          <!-- feedback de erro da duração -->
          <div class="text-danger mt-1" v-if="errors.duracao">{{ errors.duracao }}</div>
        </div>

          <!-- Status -->
          <div class="form-group">
            <label for="status">Status <span class="required">*</span></label>
            <div class="form-switch">
              <input
                type="checkbox"
                id="status"
                v-model="plano.ativo"
                class="form-check-input"
              />
              <label for="status" class="form-check-label">
                {{ plano.ativo ? "Ativo" : "Inativo" }}
              </label>
            </div>
          </div>
        </div>

      </FormSection>

      <FormSection title="Descrição do Tipo de Plano">
        <div class="form-group mt-3 w-100">
          <label for="descricao">Descrição</label>
          <textarea
            id="descricao"
            v-model="plano.descricao"
            class="form-control"
            placeholder="Digite a descrição do plano"
            rows="3"
            style="width: 100%;"
          ></textarea>
        </div>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "@/services/axios";
import BaseForm from "@/components/Layout/BaseForm.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";

export default {
  components: { BaseForm, FormSection, InputField, SelectField },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Model base (duracao SEMPRE EM DIAS ao salvar)
    const plano = reactive({
      id: null,
      nome: "",
      duracao: null,     // Em dias (back-end)
      descricao: "",
      ativo: true,
    });

    // Opções padrão (dias)
    const opcoesDuracao = ref([
      { value: 1, text: "Diário (1 dia)" },
      { value: 7, text: "Semanal (7 dias)" },
      { value: 14, text: "Quinzenal (14 dias)" },
      { value: 30, text: "Mensal (30 dias)" },
      { value: 60, text: "Bimestral (60 dias)" },
      { value: 90, text: "Trimestral (90 dias)" },
      { value: 120, text: "Quadrimestral (120 dias)" },
      { value: 180, text: "Semestral (180 dias)" },
      { value: 365, text: "Anual (365 dias)" },
    ]);

    // Controles de UI para a duração
    const usarPersonalizada = ref(false);
    const duracaoPredefinida = ref(""); // string para casar com SelectField e placeholder
    const valorCustom = ref(null);
    const unidadeCustom = ref("meses");

    // Unidades e conversão para DIAS
    const unidades = [
      { value: "dias", text: "dias", mult: 1 },
      { value: "semanas", text: "semanas", mult: 7 },
      { value: "meses", text: "meses", mult: 30 },   // aproximação prática
      { value: "anos", text: "anos", mult: 365 },
    ];

    // Calcula a duração efetiva em DIAS
    const duracaoEmDias = computed(() => {
      if (!usarPersonalizada.value) {
        const v = Number(duracaoPredefinida.value);
        return Number.isFinite(v) && v > 0 ? v : null;
      }
      const u = unidades.find((x) => x.value === unidadeCustom.value);
      const qtd = Number(valorCustom.value);
      if (!u || !Number.isFinite(qtd) || qtd <= 0) return null;
      return qtd * u.mult;
    });

    // Erros/validação
    const errors = reactive({});
    const isSubmitting = ref(false);

    const validateForm = () => {
      errors.nome = plano.nome ? "" : "O nome é obrigatório.";
      errors.duracao = duracaoEmDias.value ? "" : "A duração é obrigatória.";
    };

    const isFormValid = computed(() => {
      return Boolean(
        !Object.values(errors).some((e) => e) &&
          plano.nome &&
          duracaoEmDias.value
      );
    });

    // Sincroniza o model (em DIAS) com os controles
    const sincronizarDuracao = () => {
      plano.duracao = duracaoEmDias.value;
    };

    // Carrega registro (edição)
    const carregarPlano = async () => {
      const id = route.params.id;
      if (!id) return;

      try {
        const { data } = await axios.get(`/cadastros/tipos-planos/${id}`);
        plano.id = data.id;
        plano.nome = data.nome || "";
        plano.descricao = data.descricao || "";
        plano.ativo = data.ativo !== false;

        // Ajusta controles de duração a partir de "dias"
        const dias = Number(data.duracao);
        if (opcoesDuracao.value.some((o) => o.value === dias)) {
          usarPersonalizada.value = false;
          duracaoPredefinida.value = String(dias);
          plano.duracao = dias;
        } else {
          usarPersonalizada.value = true;
          if (dias % 365 === 0) {
            unidadeCustom.value = "anos";
            valorCustom.value = dias / 365;
          } else if (dias % 30 === 0) {
            unidadeCustom.value = "meses";
            valorCustom.value = dias / 30;
          } else if (dias % 7 === 0) {
            unidadeCustom.value = "semanas";
            valorCustom.value = dias / 7;
          } else {
            unidadeCustom.value = "dias";
            valorCustom.value = dias || null;
          }
          sincronizarDuracao();
        }
      } catch (error) {
        console.error("Erro ao carregar tipo de plano:", error);
      }
    };

    const salvarPlano = async () => {
      if (isSubmitting.value) return;

      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }

      isSubmitting.value = true;
      try {
        sincronizarDuracao(); // garante plano.duracao (em DIAS)

        const method = plano.id ? "put" : "post";
        const url = plano.id
          ? `/cadastros/tipos-planos/${plano.id}`
          : `/cadastros/tipos-planos`;

        const payload = {
          id: plano.id,
          nome: plano.nome,
          duracao: plano.duracao, // DIAS
          descricao: plano.descricao || null,
          ativo: !!plano.ativo,
        };

        await axios[method](url, payload);
        router.push("/cadastros/tipos-planos");
      } catch (error) {
        console.error("Erro ao salvar tipo de plano:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/cadastros/tipos-planos");
    };

    onMounted(() => {
      carregarPlano();
    });

    return {
      plano,
      opcoesDuracao,
      // duração personalizada
      usarPersonalizada,
      duracaoPredefinida,
      valorCustom,
      unidadeCustom,
      unidades,
      duracaoEmDias,

      errors,
      isFormValid,
      isSubmitting,
      salvarPlano,
      voltar,
      sincronizarDuracao,
    };
  },
};
</script>

<style scoped>
.form-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-check-label {
  font-size: smaller;
}
</style>
